import { H4, Classes } from '@blueprintjs/core'
import React, { useState } from 'react'
import useTicket from './hooks/useTicket'
import { Flex, EmployeeSelect, IconButton, Confirmation, Avatar, Toaster, Dialog } from '@ui'
import EmployeeWithDetailsPopover from '@ui/EmployeeWithDetailsPopover'
import StatusSelect, { getTicketStatus } from './StatusSelect'
import PrioritySelect from './PrioritySelect'
import { TicketForm } from './TicketForm'
import TicketNumber from './TicketNumber'
import LocationTag from './LocationTag'
import SourceTag from './SourceTag'
import api, { ITicketDetails } from '@api'
import track from '@track'
import Event from '@track/Event'
import { get, omit } from 'lodash'
import moment from 'moment'
import { NavigateFn } from '@reach/router'
import { ApolloError } from 'apollo-boost'
import { SERVER_DATETIME_FMT } from '@constants'
import { FormTicket } from '@api/ticket/create'
import css from './ticketManagement.module.scss'

export default function TicketsDetailsDialog({
  ticketId,
  reload,
  navigate,
}: {
  ticketId: string
  navigate: NavigateFn
  reload?: () => void
}) {
  const { ticket, isLoading, update } = useTicket(ticketId)
  const [dirty, setDirty] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const onClose = () => navigate && navigate(window.location.pathname)

  function commitUpdate(t: FormTicket): Promise<any> {
    const updatedTicket: ITicketDetails = {
      ...(ticket as ITicketDetails),
      ...t,
    }
    update(updatedTicket) // Update details

    return api.ticket
      .update(updatedTicket)
      .then(() => {
        reload && reload()
        setDirty(false)
        setEditMode(false)
        Toaster.success({ message: 'Ticket updated' })
      })
      .catch((error: ApolloError) => Toaster.fail({ error }))
  }

  return editMode ? (
    <Dialog
      isOpen
      style={{ width: 700, position: 'relative' }}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      transitionDuration={200}
      title="Update ticket"
      isCloseButtonShown={false}
      onClose={onClose}
      toolbar={<DialogToolbar onClose={onClose} />}
      body={
        <div className={Classes.DIALOG_BODY}>
          {isLoading ? (
            <TicketGhost onClose={onClose} />
          ) : (
            <TicketForm
              dirty={dirty}
              setDirty={() => setDirty(true)}
              ticket={omit(ticket as ITicketDetails, [
                'ticketNumber',
                'externalUrl',
                'employee',
                'source',
                'createdDate',
              ])}
              onCancel={() => {
                setDirty(false)
                setEditMode(false)
              }}
              onSubmit={(updatedTicket) => commitUpdate(updatedTicket)}
            />
          )}
        </div>
      }
    />
  ) : (
    <Dialog
      isOpen
      style={{ width: 700 }}
      transitionDuration={200}
      canEscapeKeyClose={false}
      isCloseButtonShown={false}
      title=""
      onClose={onClose}
      toolbar={
        !ticket || ticket.id !== ticketId ? (
          <TicketGhost onClose={onClose} />
        ) : (
          <DialogToolbar
            // ticket={ticket}
            onEdit={() => {
              setEditMode(true)
              track.tickets({ eventName: Event.EDIT_RECORD_DIALOG })
            }}
            onDelete={() => {
              api.ticket
                .remove(ticketId as string)
                .then(() => {
                  if (reload) {
                    reload()
                  }
                  onClose()
                })
                .then(() => Toaster.success({ message: 'Ticket removed' }))
                .catch((error: ApolloError) => Toaster.fail({ error }))
            }}
            onClose={onClose}
          />
        )
      }
      body={
        <div className={Classes.DIALOG_BODY}>
          {!ticket || ticket.id !== ticketId ? (
            <TicketGhost onClose={onClose} />
          ) : (
            <>
              <div>
                {ticket.employee && (
                  <Flex align="center">
                    <Avatar
                      name={ticket.employee.name}
                      assets={ticket.employee.assets}
                      size={Avatar.Size.XS}
                    />
                    <EmployeeWithDetailsPopover
                      employee={ticket.employee}
                      buttonProps={{ minimal: true, small: true }}
                    />
                    {ticket.createdDate && (
                      <span
                        style={{
                          fontWeight: 'normal',
                          color: 'var(--grey1)',
                        }}>
                        {' '}
                        created {moment.utc(ticket.createdDate, SERVER_DATETIME_FMT).fromNow()}
                      </span>
                    )}
                  </Flex>
                )}
                <div style={{ margin: '20px 0' }}>
                  <section className={css.section}>
                    <div
                      data-field="title"
                      style={{
                        margin: 0,
                        fontSize: 32,
                        lineHeight: '1.2em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                      {ticket.shortDescription}
                    </div>
                    <div style={{ margin: '14px 0' }}>
                      <TicketNumber ticket={ticket} />
                      <LocationTag
                        ticket={ticket}
                        tagProps={{
                          style: { marginLeft: 8 },
                        }}
                      />
                      <SourceTag
                        ticket={ticket}
                        tagProps={{
                          style: { marginLeft: 8 },
                        }}
                      />
                    </div>
                    <InlineActions ticket={ticket} commitUpdate={commitUpdate} />
                  </section>
                </div>
                <section className={css.section} style={{ minHeight: 200 }}>
                  <H4>Description</H4>
                  <div className={css.description} data-field="description">
                    {ticket.description}
                  </div>
                </section>
              </div>
            </>
          )}
        </div>
      }></Dialog>
  )
}

function InlineActions({
  ticket,
  commitUpdate,
}: {
  ticket: ITicketDetails
  commitUpdate: (ticket: ITicketDetails) => Promise<any>
}) {
  const items = [
    {
      label: 'Priority',
      render: () => (
        <PrioritySelect
          buttonProps={{ 'data-button': 'priority_select' }}
          value={ticket.severity}
          onChange={(severity) => {
            commitUpdate({ ...ticket, severity })
            track.ticketDetails({
              eventName: Event.INLINE_ACTION,
              props: {
                actionName: 'Severity',
                actionValue: severity,
              },
            })
          }}
        />
      ),
    },
    {
      label: 'Status',
      render: () => (
        <StatusSelect
          buttonProps={{ 'data-button': 'status_select' }}
          value={getTicketStatus(ticket.status)}
          onChange={(status) => {
            if (status) {
              commitUpdate({ ...ticket, status: status.value })
              track.ticketDetails({
                eventName: Event.INLINE_ACTION,
                props: {
                  actionName: 'Status',
                  actionValue: status.label,
                },
              })
            }
          }}
        />
      ),
    },
    {
      label: 'Assigned to',
      render: () => (
        <div className={css.employee_select_wrapper}>
          <EmployeeSelect
            clearable
            value={ticket.assignedTo}
            onChange={(assignedTo) => {
              commitUpdate({ ...ticket, assignedTo })
              track.ticketDetails({
                eventName: Event.INLINE_ACTION,
                props: {
                  actionName: 'Assigned to',
                  actionValue: get(assignedTo, 'name', null),
                },
              })
            }}
          />
        </div>
      ),
    },
  ]

  return (
    <Flex>
      {items.map(({ label, render }, i) => (
        <Flex key={`inline-show-${i}`} align="center" style={{ marginRight: 30, marginTop: 14 }}>
          <div
            style={{
              fontSize: 14,
              paddingRight: 6,
              color: 'var(--grey1)',
            }}>
            {label}
          </div>
          {render()}
        </Flex>
      ))}
    </Flex>
  )
}

function TicketGhost({ onClose }: { onClose: () => void }) {
  const placeholder = <div className={Classes.SKELETON}>Placeholder text</div>

  return (
    <>
      <DialogToolbar onClose={onClose} />
      <div>
        <Flex align="center">
          <Avatar skeleton size={Avatar.Size.XS} />
          <div style={{ marginLeft: 4 }}>{placeholder}</div>
        </Flex>
        <div style={{ margin: '20px 0' }}>
          <section className={css.section}>
            <div style={{ margin: 0, fontSize: 32 }}>{placeholder}</div>
            <div style={{ margin: '14px 0' }}>{placeholder}</div>
          </section>
        </div>
        <section className={css.section}>
          <H4>Description</H4>
          <div style={{ height: 141 }} className={Classes.SKELETON} />
        </section>
      </div>
    </>
  )
}

function DialogToolbar({
  onEdit,
  onDelete,
  onClose,
}: {
  onEdit?: () => void
  onDelete?: () => void
  onClose?: () => void
}) {
  return (
    <div className="dialog-buttons">
      {onEdit && (
        <IconButton
          data-button="edit_ticket"
          minimal
          iconName="pen"
          tooltip="Edit"
          onClick={onEdit}
        />
      )}
      {onDelete && (
        <Confirmation
          text="Delete this ticket?"
          onConfirm={onDelete}
          track={() => {
            track.tickets({ eventName: Event.DELETE_RECORD_DIALOG })
          }}>
          <IconButton data-button="delete_ticket" minimal tooltip="Delete" iconName="trash" />
        </Confirmation>
      )}
      {onClose && (
        <IconButton
          data-button="close_dialog"
          minimal
          tooltip="Close"
          iconName="times"
          onClick={onClose}
        />
      )}
    </div>
  )
}
